import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { setInitial } from "../../store/slices/chatSlice";
import { useLang } from "../../context/LanguageContext";
import DashboardService from "../../services/dashboard";
import { toastError, toastSuccess } from "../../components/ToastNotification";

const TRANSLATIONS = {
  EN: {
    title: " TOKEN SET",
    labelMonthly: "MONTHLY TOKEN SET:",
    labelPurchase: "TOKEN PURCHASE:",
    save: "SAVE",
    toastTokenSaved: "Token config saved successfully!",
  },
  ES: {
    title: "CONFIGURACIÓN MENSUAL DE TOKENS",
    labelMonthly: "CONFIGURACIÓN MENSUAL DE TOKENS:",
    labelPurchase: "COMPRA DE TOKENS:",
    save: "GUARDAR",
    toastTokenSaved: "¡Configuración de tokens guardada exitosamente!",
  },
};

/* Main page  */
const Dashboard = () => {
  const dispatch = useDispatch();
  const { lang } = useLang();
  const t = TRANSLATIONS[lang];

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [basicPlanToken, setBasicPlanToken] = useState("1000000");
  const [purchaseToken, setPurchaseToken] = useState("1000000");

  useEffect(() => {
    dispatch(setInitial(true));
    fetchTokenConfig();
  }, []);

  const fetchTokenConfig = async () => {
    setLoading(true);
    try {
      const response = await DashboardService.getTokenConfig();
      const data = response?.data?.data;
      if (data?.basic_plan_token != null) setBasicPlanToken(String(data.basic_plan_token));
      if (data?.purchase_token != null) setPurchaseToken(String(data.purchase_token));
    } catch (_) { } finally {
      setLoading(false);
    }
  };

  const handleTokenSave = async () => {
    setSaving(true);
    try {
      const body = {};
      if (basicPlanToken !== "") body.basic_plan_token = Number(basicPlanToken);
      if (purchaseToken !== "") body.purchase_token = Number(purchaseToken);
      await DashboardService.setTokenConfig(body);
      toastSuccess(t.toastTokenSaved);
    } catch (error) {
      toastError(error?.response?.data?.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col w-full h-full overflow-hidden">
      <div className="flex flex-col flex-1 min-h-0 overflow-y-auto p-4 lg:p-6 gap-5">

        {/* Header */}
        <div className="flex items-center flex-shrink-0">
          <h1 className="font-roboto font-bold text-xl lg:text-2xl text-primary uppercase tracking-wide">
            {t.title}
          </h1>
        </div>

        {/* Monthly Token */}
        <div className="h-[60px] flex items-center justify-between border border-gray-200 rounded-xl px-5 bg-gray-50 w-full gap-3 flex-shrink-0">
          <span className="font-roboto text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
            {t.labelMonthly}
          </span>
          {loading ? (
            <div className="w-36 h-[34px] rounded-lg skeleton-bone" />
          ) : (
            <input
              type="number"
              min="0"
              step="1000"
              value={basicPlanToken}
              onChange={(e) => setBasicPlanToken(e.target.value)}
              className="w-36 h-[34px] bg-white border border-primary/20 rounded-lg px-3 font-roboto text-sm text-primary font-semibold focus:outline-none focus:ring-2 focus:ring-primary/30 text-center"
              placeholder="—"
            />
          )}
        </div>
        
        {/* Token Purchase */}
        <div className="h-[60px] flex items-center justify-between border border-gray-200 rounded-xl px-5 bg-gray-50 w-full gap-3 flex-shrink-0">
          <span className="font-roboto text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
            {t.labelPurchase}
          </span>
          {loading ? (
            <div className="w-36 h-[34px] rounded-lg skeleton-bone" />
          ) : (
            <input
              type="number"
              min="0"
              step="1000"
              value={purchaseToken}
              onChange={(e) => setPurchaseToken(e.target.value)}
              className="w-36 h-[34px] bg-white border border-primary/20 rounded-lg px-3 font-roboto text-sm text-primary font-semibold focus:outline-none focus:ring-2 focus:ring-primary/30 text-center"
              placeholder="—"
            />
          )}
        </div>

        {/* Save */}
        <div className="flex justify-end flex-shrink-0">
          {loading ? (
            <div className="w-[120px] h-[40px] rounded-xl skeleton-bone-primary" />
          ) : (
            <button
              onClick={handleTokenSave}
              disabled={saving}
              className="px-10 h-[40px] rounded-xl font-roboto font-bold text-sm text-white bg-primary hover:bg-primary/90 transition-all uppercase tracking-wide disabled:opacity-60 min-w-[120px] flex items-center justify-center"
            >
              {saving ? <div className="loader" /> : t.save}
            </button>
          )}
        </div>

      </div>
    </div>
  );
};

export default Dashboard;
